import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { supabase } from "@/lib/supabase";

type Client = {
  id: string;
  name: string;
  logo_url: string | null;
  website: string | null;
  active: boolean;
  display_order: number;
  created_at: string;
};

export default function ClientesAdminPage() {
  const [items, setItems] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Client> | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("clients")
      .select("*")
      .order("display_order", { ascending: true });
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => {
    setEditing({ name: "", logo_url: "", website: "", active: true, display_order: items.length });
    setShowModal(true);
  };
  const openEdit = (c: Client) => { setEditing(c); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!editing?.name) return;
    setSaving(true);
    const payload = {
      name: editing.name,
      logo_url: editing.logo_url || null,
      website: editing.website || null,
      active: editing.active ?? true,
      display_order: editing.display_order ?? 0,
    };
    if (editing.id) {
      await supabase.from("clients").update(payload).eq("id", editing.id);
    } else {
      await supabase.from("clients").insert(payload);
    }
    setSaving(false);
    closeModal();
    fetchData();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("clients").delete().eq("id", id);
    setDeleteId(null);
    fetchData();
  };

  const toggleActive = async (c: Client) => {
    await supabase.from("clients").update({ active: !c.active }).eq("id", c.id);
    fetchData();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Clientes / Parceiros</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} cliente(s) cadastrado(s)</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          Novo Cliente
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 flex items-center justify-center text-[#1B1F5E]">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-building-line text-4xl"></i>
            </div>
            <p className="text-sm font-medium mb-1">Nenhum cliente cadastrado</p>
            <p className="text-xs text-gray-400">Adicione clientes para exibir as logomarcas na home</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Logomarca</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Nome</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Website</th>
                  <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ordem</th>
                  <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ativo</th>
                  <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      {c.logo_url ? (
                        <div className="w-28 h-12 border border-gray-100 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center p-2">
                          <img src={c.logo_url} alt={c.name} className="max-w-full max-h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-28 h-12 border border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                          <i className="ri-image-line text-gray-300 text-xl"></i>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[#1a1a1a]">{c.name}</td>
                    <td className="px-4 py-3">
                      {c.website ? (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="nofollow noreferrer"
                          className="text-[#1B1F5E] text-xs hover:underline cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-external-link-line text-xs"></i>
                          {c.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-500">{c.display_order}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(c)}
                        className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${c.active ? "bg-[#1B1F5E]" : "bg-gray-200"}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${c.active ? "left-5" : "left-0.5"}`}></span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEdit(c)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1B1F5E] transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteId(c.id)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="mt-4 bg-[#1B1F5E]/5 border border-[#1B1F5E]/20 rounded-lg p-4 flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center text-[#1B1F5E] shrink-0 mt-0.5">
          <i className="ri-information-line text-base"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1B1F5E] mb-1">Como funciona</p>
          <p className="text-xs text-gray-600 leading-relaxed">
            Os clientes cadastrados aqui aparecem na seção de logomarcas da página inicial. 
            Adicione a URL da logomarca (preferencialmente PNG com fundo transparente) e o link do site do cliente. 
            Use a ordem de exibição para controlar a sequência das logos.
          </p>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1a1a1a]">
                {editing.id ? "Editar Cliente" : "Novo Cliente"}
              </h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Nome da Empresa *</label>
                <input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]"
                  placeholder="Ex: Grupo Metalúrgico Alfa"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">URL da Logomarca</label>
                <input
                  value={editing.logo_url ?? ""}
                  onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]"
                  placeholder="https://exemplo.com/logo.png"
                />
                <p className="text-xs text-gray-400 mt-1">Recomendado: PNG com fundo transparente, proporção 3:1 (ex: 300x100px)</p>
                {editing.logo_url && (
                  <div className="mt-2 w-full h-20 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center p-3">
                    <img src={editing.logo_url} alt="preview" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Website do Cliente</label>
                <input
                  value={editing.website ?? ""}
                  onChange={(e) => setEditing({ ...editing, website: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]"
                  placeholder="https://www.empresa.com.br"
                />
                <p className="text-xs text-gray-400 mt-1">Ao clicar na logomarca, o visitante será redirecionado para este link</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Ordem de Exibição</label>
                <input
                  type="number"
                  value={editing.display_order ?? 0}
                  onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]"
                  min={0}
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active ?? true}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4 accent-[#1B1F5E]"
                />
                <span className="text-sm text-[#333]">Exibir na home</span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editing.name}
                className="flex-1 py-2.5 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60 transition-colors"
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-2">Remover cliente?</h3>
            <p className="text-gray-500 text-sm mb-6">A logomarca deixará de aparecer na home. Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap">
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
